import { cx } from "@jugl-web/utils";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  KeyboardEvent,
  FC,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import {
  BOLD_STAR,
  ITALIC_STAR,
  STRIKETHROUGH,
  UNORDERED_LIST,
  ORDERED_LIST,
  CODE,
  INLINE_CODE,
} from "@lexical/markdown";
import { MentionTextareaControllerPlugin } from "./plugins/MentionTextareaControllerPlugin";
import { MentionsPlugin } from "./plugins/MentionsPlugin";
import { OnEnterPlugin } from "./plugins/OnEnterPlugin";
import { MentionTextareaHandle, MentionTextareaProps } from "./types";
import { getLexicalConfig } from "./utils";
import { RemoteMentionsPlugin } from "./plugins/RemoteMentionsPlugin";
import { MaxLengthPlugin } from "./plugins/MaxLengthPlugin";
import { RestrictedPastePlugin } from "./plugins/RestrictedPastePlugin";

const EditableStatePlugin: FC<{ isDisabled: boolean }> = ({ isDisabled }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(!isDisabled);
  }, [editor, isDisabled]);
  return null;
};

export const CHAT_TEXT_AREA_NAMESPACE = "chat-textarea";

export const ChatTextarea = forwardRef<
  MentionTextareaHandle,
  MentionTextareaProps
>(
  (
    {
      externalLexicalComposer,
      mentions,
      mentionListType = "container-pinned",
      isDisabled = false,
      placeholder,
      classes,
      maxTextLength,
      onRawTextChange,
      onPaste,
      onEnter,
      onEscape,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const config = useMemo(
      () => getLexicalConfig(CHAT_TEXT_AREA_NAMESPACE, isDisabled),
      [isDisabled]
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        onEscape?.(event);
      }
    };

    const content = (
      <>
        <div className={cx("relative", classes?.container)}>
          <div ref={containerRef} />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={cx("p-2", classes?.contentEditable)}
                onPaste={onPaste}
                onKeyDown={handleKeyDown}
              />
            }
            placeholder={
              placeholder ? (
                <div
                  className={cx(
                    "pointer-events-none absolute left-2 top-2 select-none overflow-hidden",
                    classes?.placeholder
                  )}
                >
                  {placeholder}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <MarkdownShortcutPlugin
          transformers={[
            BOLD_STAR,
            ITALIC_STAR,
            STRIKETHROUGH,
            UNORDERED_LIST,
            ORDERED_LIST,
            CODE,
            INLINE_CODE,
          ]}
        />
        <HistoryPlugin />
        <RestrictedPastePlugin editorNamespace={CHAT_TEXT_AREA_NAMESPACE} />
        {mentions?.type === "manual" && (
          <MentionsPlugin
            mentions={mentions.mentions}
            listType={mentionListType}
            containerRef={containerRef}
          />
        )}
        {mentions?.type === "remote" && (
          <RemoteMentionsPlugin
            fetchParams={mentions.fetchParams}
            listType={mentionListType}
            containerRef={containerRef}
            meId={mentions.meId}
          />
        )}
        <MentionTextareaControllerPlugin
          handle={ref}
          onRawTextChange={onRawTextChange}
        />
        <OnEnterPlugin onEnter={onEnter} />
        <ListPlugin />
        {maxTextLength && <MaxLengthPlugin maxTextLength={maxTextLength} />}
        <EditableStatePlugin isDisabled={isDisabled} />
      </>
    );

    return externalLexicalComposer ? (
      content
    ) : (
      <LexicalComposer initialConfig={config}>{content}</LexicalComposer>
    );
  }
);
